import type { Vector3 } from "three";
import { Vector2 } from "three";
import { LinearCameraCal } from "./linear_camera_cal";
import { degToRad } from "three/src/math/MathUtils";

/**
 * A class that inherits from LinearCameraCal and also includes the image size.
 * This allows computation of the field of view and the use of normalized coordinates.
 */
export class LinearCameraModel extends LinearCameraCal {
  image_size: Vector2;

  constructor(focal_length: Vector2, principal_point: Vector2, image_size: Vector2) {
    super(focal_length, principal_point);
    this.image_size = image_size;
  }

  camera_ray_from_normalized_coords(normalized_coords: Vector2): [Vector3, number] {
    // normalized coords are in the range [0-1] relative to top left of image
    const pixel = new Vector2(
      normalized_coords.x * this.image_size.x,
      normalized_coords.y * this.image_size.y
    );
    return this.camera_ray_from_pixel(pixel);
  }

  get_field_of_view(): Vector2 {
    // horizontal fov
    const center_left = this.camera_ray_from_normalized_coords(new Vector2(0, 0.5))[0];
    const center_right = this.camera_ray_from_normalized_coords(new Vector2(1, 0.5))[0];
    const horizontal_fov = center_left.angleTo(center_right);
    // vertical fov
    const center_top = this.camera_ray_from_normalized_coords(new Vector2(0.5, 0.0))[0];
    const center_bottom = this.camera_ray_from_normalized_coords(new Vector2(0.5, 1.0))[0];
    const vertical_fov = center_top.angleTo(center_bottom);

    return new Vector2(horizontal_fov, vertical_fov);
  }

  get_aspect_ratio(): number {
    return this.image_size.x / this.image_size.y;
  }

  static fromVFOV(width: number, height: number, vfov_deg: number): LinearCameraModel {
    const focalLength = height / Math.tan(degToRad(vfov_deg) / 2.0) / 2.0;
    return new LinearCameraModel(
      new Vector2(focalLength, focalLength),
      new Vector2(width / 2, height / 2),
      new Vector2(width, height)
    );
  }

  static fromFocalLength(
    width: number,
    height: number,
    focalLengthH: number,
    focalLengthV: number
  ): LinearCameraModel {
    return new LinearCameraModel(
      new Vector2(focalLengthH, focalLengthV),
      new Vector2(width / 2, height / 2),
      new Vector2(width, height)
    );
  }

  override clone(): LinearCameraModel {
    return new LinearCameraModel(
      this.focal_length.clone(),
      this.principal_point.clone(),
      this.image_size.clone()
    );
  }
}
